import { Box, HorizontalSpacer, SectionContainer } from 'components'
import { CloseIcon } from 'components/CloseIcon'
import { useSupportedWeb3 } from 'components/SupportedChainsWrapper'
import { Colors } from 'constants/colors'
import { useAnalytics, useCreditContext, useModal } from 'contexts'
import { CoreCapsuleWithAccount } from 'entities'
import { BigNumber } from 'ethers'
import { useAsync, useCapsulesLoading, useResponsive } from 'hooks'
import { useEffect, useState } from 'react'
import { formatBigNumber, getIconUrlFromGCP, getSpacing, reduceValue } from 'utils'
import { ACTION_TYPES, BORROW_ACTIONS } from './borrowActions'
import { BorrowInput, OptimizedAction } from './BorrowInput'
import { BottomStats } from './BottomStats'
import { getFutureValues } from './helpers/getFutureValues'
import { isPotentialExit } from './helpers/isPotentialExit'
import { isRiskyAction } from './helpers/isRiskyAction'
import { LTVUsage } from './LTVUsage'
import { SubmitArea } from './SubmitArea'
import { TopStats } from './TopStats'
import { UserBorrowStats } from './UserBorrowStats'

export type MaxValues = {
  [ACTION_TYPES.DEPOSIT]?: BigNumber
  [ACTION_TYPES.BORROW]?: BigNumber
  [ACTION_TYPES.REPAY]?: BigNumber
  [ACTION_TYPES.WITHDRAW]?: BigNumber
}

export interface IVaultModalProps {
  coreCapsule: CoreCapsuleWithAccount
}

export const VaultModal = ({ coreCapsule }: IVaultModalProps) => {
  const { closeModal } = useModal()
  const [isExitTransaction, setIsExitTransaction] = useState(false)
  const { width } = useResponsive()
  const { account, signer } = useSupportedWeb3()
  const { poolCapsules } = useCreditContext()
  const isCapsuleLoading = useCapsulesLoading([coreCapsule])
  const [dropdownOption, setDropdownOption] = useState<typeof BORROW_ACTIONS[0]>(BORROW_ACTIONS[0])
  const [inputValue, setInputValue] = useState<BigNumber | null>(null)
  const { track } = useAnalytics()

  const poolCapsule = poolCapsules.get(coreCapsule.borrowPoolAddress)

  const [maxValues, , loadingMaxValues] = useAsync<MaxValues>(async () => {
    if (!account || !coreCapsule || !signer) {
      return {
        [ACTION_TYPES.DEPOSIT]: undefined,
        [ACTION_TYPES.BORROW]: undefined,
        [ACTION_TYPES.REPAY]: undefined,
        [ACTION_TYPES.WITHDRAW]: undefined,
      }
    }

    // Deposit & repay
    const maxDeposit = coreCapsule.accountProperties.accountCollateralBalance

    // Borrow
    const maxBorrow = coreCapsule.getAccountMaxBorrowAmount(
      poolCapsule,
      poolCapsule.supportedDepositAssets[0].address,
    )

    // Repay
    const maxRepay = coreCapsule.getAccountMaxRepayAmount(
      poolCapsule,
      poolCapsule.supportedDepositAssets[0].address,
    )

    // Withdraw
    const maxWithdraw = coreCapsule.getAccountMaxWithdrawAmount()

    return {
      [ACTION_TYPES.DEPOSIT]: maxDeposit,
      [ACTION_TYPES.BORROW]: maxBorrow,
      [ACTION_TYPES.REPAY]: maxRepay,
      [ACTION_TYPES.WITHDRAW]: maxWithdraw,
    }
  }, [coreCapsule, account, signer, isCapsuleLoading])

  // Empty the input value if the core is refreshing
  useEffect(() => {
    if (isCapsuleLoading) {
      setInputValue(null)
    }
  }, [isCapsuleLoading])

  const onOptimizeBtnClicked = (optimizedAction: OptimizedAction) => {
    const { decimals, symbol } = poolCapsule.supportedDepositAssets[0]
    track('Optimized', {
      action: optimizedAction.action.label,
      amount: formatBigNumber(optimizedAction.scaledValue),
      asset: symbol,
    })
    setDropdownOption(optimizedAction.action)
    setInputValue(reduceValue(optimizedAction.scaledValue, decimals))
  }

  const isLoading = loadingMaxValues || isCapsuleLoading
  const isMobile = width < 800

  const creditLimitAttributes = coreCapsule.creditLimitAttributes
  const title = creditLimitAttributes?.title

  let decimals = 18
  let symbol = coreCapsule.collateralSymbol
  const borrowAssetInfo = poolCapsule.supportedDepositAssets[0]

  if (dropdownOption.value === ACTION_TYPES.BORROW || dropdownOption.value === ACTION_TYPES.REPAY) {
    decimals = borrowAssetInfo.decimals
    symbol = borrowAssetInfo.symbol
  }

  const futureValues = getFutureValues(
    coreCapsule,
    poolCapsule,
    dropdownOption.value,
    borrowAssetInfo,
    inputValue,
  )

  const isRiskyResult = isRiskyAction(inputValue, coreCapsule, futureValues.futureLTV)
  const isPotentialExitTx = isPotentialExit(dropdownOption, inputValue, maxValues)

  return (
    <SectionContainer
      style={{ ...(isMobile ? { width: '80vw' } : { minWidth: '560px', width: '40vw' }) }}
    >
      <Box
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
        marginBottom={getSpacing('m')}
      >
        <Box flexDirection="row" alignItems="center">
          <img src={getIconUrlFromGCP(coreCapsule.collateralSymbol.toLowerCase())} width="26px" />
          <Box marginLeft={getSpacing('s')}>{title}</Box>
        </Box>
        <CloseIcon onClick={closeModal} width={24} color={Colors.TEXT_DARK} />
      </Box>
      <TopStats coreCapsule={coreCapsule} />
      <HorizontalSpacer />
      <BorrowInput
        coreCapsule={coreCapsule}
        poolCapsule={poolCapsule}
        onOptimizeBtnClicked={onOptimizeBtnClicked}
        symbol={symbol}
        dropdownOptions={BORROW_ACTIONS}
        inputValue={inputValue}
        onInputChange={setInputValue}
        dropdownValue={dropdownOption.value}
        onDropdownChange={({ label, value }) => {
          setInputValue(undefined)
          setDropdownOption({ label, value: ACTION_TYPES[value] })
        }}
        isMobile={isMobile}
        maxValue={maxValues && maxValues[dropdownOption.value]}
        decimals={decimals}
      />
      <HorizontalSpacer />
      <LTVUsage
        coreCapsule={coreCapsule}
        futureLTV={futureValues.futureLTV}
        isAtRisk={isRiskyResult}
        isExitTransaction={isExitTransaction}
      />
      <HorizontalSpacer />
      <UserBorrowStats
        coreCapsule={coreCapsule}
        futureValues={futureValues}
        isExitTransaction={isExitTransaction}
      />
      <HorizontalSpacer />
      <BottomStats coreCapsule={coreCapsule} />
      <HorizontalSpacer />
      <SubmitArea
        input={inputValue}
        maxValues={maxValues}
        coreCapsule={coreCapsule}
        hasRiskPrompt={isRiskyResult}
        hasExitPrompt={isPotentialExitTx}
        loadingMaxValues={isLoading}
        action={dropdownOption}
        closeModal={closeModal}
        isExitTransaction={isExitTransaction}
        setIsExitTransaction={setIsExitTransaction}
        futureLTV={futureValues.futureLTV}
      />
    </SectionContainer>
  )
}
